import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import i18n, { LANGUAGES, getLanguage, setLanguage as applyLanguage } from "@i18n";

const SIDEBAR_STATE_KEY = "ginastee-sidebar-state";
const APPEARANCE_KEY = "system_appearence";
const MOBILE_BREAKPOINT = 1200;

const ScreenContext = createContext(null);

function resolveInitialAppearance() {
  const saved = localStorage.getItem(APPEARANCE_KEY);
  if (saved === "dark" || saved === "light" || saved === "system") return saved;
  return "light";
}

function systemPrefersDark() {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

function resolveEffectiveTheme(mode) {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode;
}

function resolveInitialSidebarOpen() {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  if (isMobile) return false;
  const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
  if (saved !== null) return JSON.parse(saved);
  return true;
}

const APPEARANCE_CYCLE = ["light", "system", "dark"];

export function ScreenProvider({ children }) {
  const [appearance, setAppearanceState] = useState(resolveInitialAppearance);
  const resolvedAppearance = resolveEffectiveTheme(appearance);
  const [sidebarOpen, setSidebarOpenState] = useState(resolveInitialSidebarOpen);
  const [screenImage, setScreenImageState] = useState(null);
  const [snackbar, setSnackbarState] = useState(null);

  // UI language ("pt" | "en" | "es"). i18next owns the source of truth and the
  // localStorage persistence; this mirror keeps consumers re-rendering.
  const [language, setLanguageState] = useState(getLanguage);

  useEffect(() => {
    const handleLanguageChanged = (lng) => setLanguageState(lng);
    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, []);

  const setLanguage = useCallback((code) => applyLanguage(code), []);

  const screenContentRef = useRef(null);

  const setAppearance = useCallback((mode = null) => {
    setAppearanceState((current) => {
      let next = current;
      if (mode === "toggle" || mode === null || mode === undefined) {
        // Cycle through light → system → dark → light
        const idx = APPEARANCE_CYCLE.indexOf(current);
        next = APPEARANCE_CYCLE[(idx + 1) % APPEARANCE_CYCLE.length];
      } else if (mode === "dark" || mode === "light" || mode === "system") {
        next = mode;
      }
      return next;
    });
  }, []);

  const toggleSidebar = useCallback((state = null) => {
    setSidebarOpenState((current) => {
      const next = state !== null && state !== undefined ? state : !current;
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setScreenImage = useCallback((url = null) => {
    setScreenImageState(url);
  }, []);

  const setSnackbar = useCallback((message) => {
    setSnackbarState(message);
  }, []);

  useEffect(() => {
    localStorage.setItem(APPEARANCE_KEY, appearance);
    const effectiveTheme = resolveEffectiveTheme(appearance);
    document.body.className = effectiveTheme;
    window.appearenceMode = effectiveTheme;
    window.dispatchEvent(
      new CustomEvent("appearence-change", { detail: effectiveTheme })
    );

    // Re-apply when the OS preference changes while 'system' is selected
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const handleMqChange = () => {
      if (appearance === "system") {
        const t = resolveEffectiveTheme("system");
        document.body.className = t;
        window.appearenceMode = t;
        window.dispatchEvent(new CustomEvent("appearence-change", { detail: t }));
      }
    };
    mq?.addEventListener("change", handleMqChange);
    return () => mq?.removeEventListener("change", handleMqChange);
  }, [appearance]);

  useEffect(() => {
    window.triggerAppearence = (value) => {
      const mode =
        value === "dark" || value === "light" || value === "system"
          ? value
          : "toggle";
      setAppearance(mode);
      return mode === "toggle" ? window.appearenceMode : resolveEffectiveTheme(mode);
    };
    window.toggleSidebar = toggleSidebar;
    window.setSnackbar = setSnackbar;
    window.setScreenImage = setScreenImage;
    window.setBackgroundEffect = () => {};
  }, [setAppearance, toggleSidebar, setSnackbar, setScreenImage]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isMobile && sidebarOpen) {
        setSidebarOpenState(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarOpen]);

  const value = useMemo(
    () => ({
      appearance,
      resolvedAppearance,
      setAppearance,
      sidebarOpen,
      toggleSidebar,
      screenImage,
      setScreenImage,
      snackbar,
      setSnackbar,
      language,
      setLanguage,
      languages: LANGUAGES,
      screenContentRef,
      MOBILE_BREAKPOINT,
    }),
    [
      appearance,
      resolvedAppearance,
      setAppearance,
      sidebarOpen,
      toggleSidebar,
      screenImage,
      setScreenImage,
      snackbar,
      setSnackbar,
      language,
      setLanguage,
    ]
  );

  return (
    <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>
  );
}

export function useScreen() {
  const ctx = useContext(ScreenContext);
  if (!ctx) {
    throw new Error("useScreen must be used within a ScreenProvider");
  }
  return ctx;
}

export default useScreen;
